import { Users, Clock, CheckCircle, XCircle, TrendingUp, TrendingDown, Info } from 'lucide-react';
import { LeadCohortMetrics as LeadCohortMetricsType } from '@/types/reports';
import { MetricCard, MetricComparison } from './MetricCard';
import { ComparisonMetrics } from '@/hooks/useComparisonMetrics';
import { calculateChange } from '@/utils/periodComparison';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface LeadCohortMetricsProps {
  metrics: LeadCohortMetricsType;
  comparisonMetrics?: ComparisonMetrics;
  comparisonLabel?: string;
  comparisonLoading?: boolean;
}

export const LeadCohortMetrics = ({ 
  metrics, 
  comparisonMetrics, 
  comparisonLabel,
  comparisonLoading 
}: LeadCohortMetricsProps) => {
  const formatPercentage = (value: number) => `${value.toFixed(1)}%`;

  // Helper to build comparison prop
  const getComparison = (
    current: number,
    previousKey: keyof ComparisonMetrics,
    isPositiveGood = true
  ): MetricComparison | undefined => {
    if (!comparisonMetrics || !comparisonLabel) return undefined;
    const change = calculateChange(current, comparisonMetrics[previousKey]);
    return { ...change, label: comparisonLabel, isPositiveGood };
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold text-foreground">Lead Metrics</h3>
        <span className="text-sm text-muted-foreground">(by Created Date)</span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Info className="h-4 w-4 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>All metrics are based on when leads were created. A lead counts toward this period regardless of when it was converted or lost.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2">
        <MetricCard
          title="Total Leads"
          value={metrics.totalLeads}
          subtitle="Received"
          icon={Users}
          valueColor="text-primary"
          iconBgColor="bg-primary/10"
          comparison={getComparison(metrics.totalLeads, 'totalLeads', true)}
          comparisonLoading={comparisonLoading}
        />
        
        <MetricCard
          title="Open"
          value={metrics.openLeads}
          subtitle="New leads"
          icon={Clock}
          valueColor="text-info"
          iconBgColor="bg-info/10"
          iconColor="text-info"
          comparison={getComparison(metrics.openLeads, 'openLeads', false)}
          comparisonLoading={comparisonLoading}
        />
        
        <MetricCard
          title="Follow Up"
          value={metrics.followUpLeads}
          subtitle="Needs action"
          icon={Clock}
          valueColor="text-warning"
          iconBgColor="bg-warning/10"
          iconColor="text-warning"
          comparison={getComparison(metrics.followUpLeads, 'followUpLeads', false)}
          comparisonLoading={comparisonLoading}
        />
        
        <MetricCard
          title="Converted"
          value={metrics.convertedLeads}
          subtitle="To jobs"
          icon={CheckCircle}
          valueColor="text-success"
          iconBgColor="bg-success/10"
          iconColor="text-success"
          comparison={getComparison(metrics.convertedLeads, 'convertedLeads', true)}
          comparisonLoading={comparisonLoading}
        />
        
        <MetricCard
          title="Lost"
          value={metrics.lostLeads}
          subtitle="Disqualified"
          icon={XCircle}
          valueColor="text-destructive"
          iconBgColor="bg-destructive/10"
          iconColor="text-destructive"
          comparison={getComparison(metrics.lostLeads, 'lostLeads', false)}
          comparisonLoading={comparisonLoading}
        />
        
        <MetricCard
          title="Conversion Rate"
          value={formatPercentage(metrics.conversionRate)}
          subtitle={`${metrics.convertedLeads}/${metrics.totalLeads}`}
          icon={TrendingUp}
          valueColor="text-success"
          iconBgColor="bg-success/10"
          iconColor="text-success"
        />
        
        <MetricCard
          title="Loss Rate"
          value={formatPercentage(metrics.lossRate)}
          subtitle={`${metrics.lostLeads}/${metrics.totalLeads}`}
          icon={TrendingDown}
          valueColor="text-destructive"
          iconBgColor="bg-destructive/10"
          iconColor="text-destructive"
        />
      </div>
    </div>
  );
};
